import React from "react";

const parseThreshold = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
};

/**
 * GaugeWidget — semicircle SVG gauge with 4-zone coloring.
 *
 * Threshold fields (from VariableData) — as labelled in the variable settings UI:
 *   medium_threshold_2 = "Min tolerated value" → red below this
 *   low_threshold_2    = "Min desired value"   → orange between min_tolerated and min_desired
 *   low_threshold      = "Max desired value"   → orange between max_desired and max_tolerated
 *   medium_threshold   = "Max tolerated value" → gauge max = medium_threshold * 1.2
 *
 * Arc zones (always visible as background):
 *   🔴 Red    — 0 → min_tolerated  and  max_tolerated → gaugeMax
 *   🟠 Orange — min_tolerated → min_desired  and  max_desired → max_tolerated
 *   🟢 Green  — min_desired → max_desired
 */
export default function GaugeWidget({ data, name }) {
    const rawValue = parseFloat(data?.value);
    const value = isNaN(rawValue) ? 0 : rawValue;

    const minTolerated = parseThreshold(data?.medium_threshold_2); // "Min tolerated value"
    const minDesired   = parseThreshold(data?.low_threshold_2);    // "Min desired value"
    const maxDesired   = parseThreshold(data?.low_threshold);      // "Max desired value"
    const maxTolerated = parseThreshold(data?.medium_threshold);   // "Max tolerated value"

    // Gauge range: extend 20% beyond max tolerated so the red zone is always visible
    const gaugeMax = maxTolerated !== null
        ? maxTolerated * 1.2
        : (value > 0 ? value * 1.5 : 100);

    const unit = data?.variable?.unit || '';
    const precision = Math.min(Number(data?.precision ?? 2), 4);
    const displayName = name || data?.name || data?.variable?.name || '';

    // Current zone color for needle pivot + value text
    const hasThresholds = maxTolerated !== null || maxDesired !== null || minDesired !== null;
    let currentColor = '#64748B';
    if (hasThresholds) {
        currentColor = '#22C55E';
        if (maxTolerated !== null && value > maxTolerated) {
            currentColor = '#EF4444';
        } else if (maxDesired !== null && value > maxDesired) {
            currentColor = '#F59E0B';
        } else if (minDesired !== null && value < minDesired) {
            const minTol = minTolerated !== null ? minTolerated : minDesired;
            currentColor = value < minTol ? '#EF4444' : '#F59E0B';
        }
    }

    // SVG parameters
    const cx = 100, cy = 100, r = 72, strokeW = 14;

    // Convert a gauge percentage (0–1) to an SVG point on the semicircle.
    // pct=0 → left end (cx-r, cy), pct=1 → right end (cx+r, cy), pct=0.5 → top (cx, cy-r)
    const arcPoint = (pct) => {
        const angle = Math.PI * (1 - pct);
        return [
            (cx + r * Math.cos(angle)).toFixed(2),
            (cy - r * Math.sin(angle)).toFixed(2),
        ];
    };

    // Build an SVG arc path string from pct1 to pct2 (both 0–1).
    // large-arc-flag is always 0: every segment spans ≤180° of the full circle.
    const arcSegPath = (pct1, pct2) => {
        if (pct2 - pct1 < 0.001) return null;
        const [x1, y1] = arcPoint(Math.max(0, Math.min(pct1, 1)));
        const [x2, y2] = arcPoint(Math.max(0, Math.min(pct2, 1)));
        return `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`;
    };

    const toPct = (v) =>
        v !== null ? Math.min(Math.max(v / gaugeMax, 0), 1) : null;

    // Zone boundary percentages
    const minTolPct = toPct(minTolerated) ?? 0;
    const minDesPct = toPct(minDesired)   ?? minTolPct;
    const maxTolPct = toPct(maxTolerated) ?? (1 / 1.2);   // ≈ 0.833 when gaugeMax = maxTol*1.2
    const maxDesPct = toPct(maxDesired)   ?? maxTolPct;

    // Build colored arc segments (butt caps — no overlap at junctions)
    const coloredSegments = [];
    if (hasThresholds && gaugeMax > 0) {
        const add = (key, pct1, pct2, color) => {
            const d = arcSegPath(pct1, pct2);
            if (d) coloredSegments.push(
                <path key={key} d={d} fill="none"
                      stroke={color} strokeWidth={strokeW} strokeLinecap="butt"/>
            );
        };

        add('r1', 0,         minTolPct, '#EF4444'); // red low
        add('o1', minTolPct, minDesPct, '#F59E0B'); // orange low
        add('g',  minDesPct, maxDesPct, '#22C55E'); // green
        add('o2', maxDesPct, maxTolPct, '#F59E0B'); // orange high
        add('r2', maxTolPct, 1,         '#EF4444'); // red high (beyond max tolerated)
    }

    // Needle
    const valuePct = gaugeMax > 0 ? Math.min(Math.max(value / gaugeMax, 0), 1) : 0;
    const needleAngle = Math.PI * (1 - valuePct);
    const needleLen = r - 4;
    const needleX = (cx + needleLen * Math.cos(needleAngle)).toFixed(1);
    const needleY = (cy - needleLen * Math.sin(needleAngle)).toFixed(1);

    const displayValue = isNaN(rawValue) ? data?.value : rawValue.toFixed(precision);
    const gaugeMaxLabel = (() => {
        const label = maxTolerated !== null ? maxTolerated : gaugeMax;
        return label % 1 === 0 ? String(label) : label.toFixed(1);
    })();

    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', width: '100%', height: '100%',
            padding: '8px 4px', boxSizing: 'border-box',
            backgroundColor: 'white', borderRadius: '8px',
            border: '1px solid #E2E8F0',
        }}>
            <div style={{
                fontSize: '13px', fontWeight: '600', color: '#334155',
                textAlign: 'center', marginBottom: '4px',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                maxWidth: '100%',
            }}>
                {displayName}
            </div>
            <svg viewBox="10 28 180 100" style={{ width: '100%', maxWidth: '220px', flex: '1', minHeight: 0 }}>
                {/* Gray background arc — provides rounded end-caps for the colored zones */}
                <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
                      fill="none" stroke="#E2E8F0" strokeWidth={strokeW} strokeLinecap="round"/>

                {/* Colored zone segments (butt caps so colors don't bleed into each other) */}
                {coloredSegments}

                {/* Needle */}
                <line x1={cx} y1={cy} x2={needleX} y2={needleY}
                      stroke="#475569" strokeWidth="2.5" strokeLinecap="round"/>
                <circle cx={cx} cy={cy} r="5" fill={currentColor}/>

                {/* Value & unit */}
                <text x={cx} y={cy - 10} textAnchor="middle" fontSize="20" fontWeight="bold" fill={currentColor}>
                    {displayValue}
                </text>
                <text x={cx} y={cy + 16} textAnchor="middle" fontSize="11" fill="#64748B">
                    {unit}
                </text>

                {/* Min / max labels */}
                <text x={cx - r - 2} y={cy + 24} textAnchor="middle" fontSize="9" fill="#94A3B8">0</text>
                <text x={cx + r + 2} y={cy + 24} textAnchor="middle" fontSize="9" fill="#94A3B8">
                    {gaugeMaxLabel}
                </text>
            </svg>
        </div>
    );
}
